import { createStore } from 'zustand/vanilla';

const createFiscalStore = (supabaseClient, getUserId) => {
  if (!supabaseClient) {
    throw new Error('Supabase client is required to initialize the fiscal store');
  }

  if (!getUserId || typeof getUserId !== 'function') {
    throw new Error('getUserId function is required to initialize the fiscal store');
  }

  try {
    const store = createStore((set, get) => ({
      accountTaxMaps: [],
      tenantRetentionAccounts: [],
      tenantFiscalConfig: null,
      retentionConcepts: [],
      chartOfAccounts: [],
      isLoading: false,
      error: null,
      isConnected: false,

      setConnectionStatus: (status) => set({ isConnected: status }),

      fetchFiscalData: async () => {
        set({ isLoading: true, error: null });
        try {
          const userId = getUserId();
          if (!userId) {
            throw new Error('User not authenticated');
          }

          const [taxMapRes, retentionAccountsRes, fiscalConfigRes, retentionConceptsRes, accountsRes, userRes] = await Promise.all([
            supabaseClient.from('invoice_account_tax_map').select('*').eq('tenant_user_id', userId),
            supabaseClient.from('invoice_tenant_retention_accounts').select('*').eq('tenant_user_id', userId),
            supabaseClient.from('invoice_tenant_fiscal_config').select('*').eq('tenant_user_id', userId).single(),
            supabaseClient.from('invoice_retention_concepts').select('*'),
            supabaseClient.from('invoice_chart_of_accounts').select('account_code, description, tax_application, is_active').eq('user_id', userId),
            supabaseClient.from('invoice_users').select('responsabilidad_fiscal').eq('id', userId).single()
          ]);

          if (taxMapRes.error) throw taxMapRes.error;
          if (retentionAccountsRes.error) throw retentionAccountsRes.error;
          if (fiscalConfigRes.error && fiscalConfigRes.error.code !== 'PGRST116') throw fiscalConfigRes.error; // PGRST116 = no rows found
          if (retentionConceptsRes.error) throw retentionConceptsRes.error;
          if (accountsRes.error) throw accountsRes.error;
          
          let tenantConf = fiscalConfigRes.data || null;
          if (tenantConf) {
            tenantConf.responsabilidad_fiscal = userRes?.data?.responsabilidad_fiscal || 'R-99-PN';
          } else if (userRes?.data?.responsabilidad_fiscal) {
            tenantConf = { responsabilidad_fiscal: userRes.data.responsabilidad_fiscal };
          }

          set({
            accountTaxMaps: taxMapRes.data || [],
            tenantRetentionAccounts: retentionAccountsRes.data || [],
            tenantFiscalConfig: tenantConf,
            retentionConcepts: retentionConceptsRes.data || [],
            chartOfAccounts: accountsRes.data || [],
            isLoading: false,
            isConnected: true,
          });
        } catch (error) {
          console.error('Error fetching fiscal data:', error.message);
          set({
            error: 'Failed to load fiscal configuration. Please try again later.',
            isLoading: false,
            isConnected: false,
          });
        }
      },

      updateAccountTaxMap: async (id, dataToUpdate) => {
        set({ error: null });
        try {
          const userId = getUserId();
          const { error } = await supabaseClient
            .from('invoice_account_tax_map')
            .update(dataToUpdate)
            .eq('id', id)
            .eq('tenant_user_id', userId);

          if (error) throw error;
          
          set((state) => ({
            accountTaxMaps: state.accountTaxMaps.map((item) =>
              item.id === id ? { ...item, ...dataToUpdate } : item
            ),
            isLoading: false,
          }));
          return { success: true };
        } catch (error) {
          console.error('Error updating account tax map:', error.message);
          set({ error: error.message, isLoading: false });
          return { success: false, error: error.message };
        }
      },

      bulkUpdateAccountTaxMap: async (ids, dataToUpdate) => {
        if (!ids || ids.length === 0) return { success: true };
        set({ isLoading: true, error: null });
        try {
          const userId = getUserId();
          const { error } = await supabaseClient
            .from('invoice_account_tax_map')
            .update(dataToUpdate)
            .in('id', ids)
            .eq('tenant_user_id', userId);

          if (error) throw error;
          
          set((state) => ({
            accountTaxMaps: state.accountTaxMaps.map((item) =>
              ids.includes(item.id) ? { ...item, ...dataToUpdate } : item
            ),
            isLoading: false,
          }));
          return { success: true };
        } catch (error) {
          console.error('Error bulk updating account tax map:', error.message);
          set({ error: error.message, isLoading: false });
          return { success: false, error: error.message };
        }
      },

      saveTenantFiscalConfig: async (configData) => {
        set({ isLoading: true, error: null });
        try {
          const userId = getUserId();
          const { tenantFiscalConfig } = get();
          
          const { responsabilidad_fiscal, ...configDataWithoutUser } = configData;
          let result;
          if (tenantFiscalConfig?.id) {
            // Update
            const { data, error } = await supabaseClient
              .from('invoice_tenant_fiscal_config')
              .update(configDataWithoutUser)
              .eq('tenant_user_id', userId)
              .select()
              .single();
              
            if (error) {
              // Compatibilidad mientras se aplica la migración de permisos de
              // cuentas de costo en todos los entornos.
              if (error.code === 'PGRST204' && error.message?.includes('allow_cost_accounts')) {
                const { allow_cost_accounts, ...compatibleData } = configDataWithoutUser;
                const retry = await supabaseClient
                  .from('invoice_tenant_fiscal_config')
                  .update(compatibleData)
                  .eq('tenant_user_id', userId)
                  .select()
                  .single();
                if (retry.error) throw retry.error;
                result = { ...retry.data, allow_cost_accounts };
              } else {
                throw error;
              }
            } else {
              result = data;
            }
          } else {
            // Insert
            const { data, error } = await supabaseClient
              .from('invoice_tenant_fiscal_config')
              .insert({ ...configDataWithoutUser, tenant_user_id: userId })
              .select()
              .single();
              
            if (error) throw error;
            result = data;
          }

          if (responsabilidad_fiscal !== undefined) {
             const { error: userError } = await supabaseClient
               .from('invoice_users')
               .update({ responsabilidad_fiscal })
               .eq('id', userId);
             if (userError) throw userError;
             result.responsabilidad_fiscal = responsabilidad_fiscal;
          }

          set({
            tenantFiscalConfig: result,
            isLoading: false,
          });
          return { success: true };
        } catch (error) {
          console.error('Error saving tenant fiscal config:', error.message);
          set({ error: error.message, isLoading: false });
          return { success: false, error: error.message };
        }
      },

      saveTenantRetentionAccount: async (conceptId, accountCode) => {
        set({ isLoading: true, error: null });
        try {
          const userId = getUserId();
          const { tenantRetentionAccounts } = get();
          
          const existing = tenantRetentionAccounts.find(a => a.retention_concept_id === conceptId);
          
          let result;
          if (existing) {
            // Update
            const { data, error } = await supabaseClient
              .from('invoice_tenant_retention_accounts')
              .update({ account_code: accountCode })
              .eq('id', existing.id)
              .select()
              .single();
            if (error) throw error;
            result = data;
          } else {
            // Insert
            const { data, error } = await supabaseClient
              .from('invoice_tenant_retention_accounts')
              .insert({ 
                tenant_user_id: userId, 
                retention_concept_id: conceptId, 
                account_code: accountCode 
              })
              .select()
              .single();
            if (error) throw error;
            result = data;
          }

          set((state) => ({
            tenantRetentionAccounts: existing 
              ? state.tenantRetentionAccounts.map(a => a.id === existing.id ? result : a)
              : [...state.tenantRetentionAccounts, result],
            isLoading: false
          }));
          return { success: true };
        } catch (error) {
          console.error('Error saving tenant retention account:', error.message);
          set({ error: error.message, isLoading: false });
          return { success: false, error: error.message };
        }
      },

      deleteTenantRetentionAccount: async (id) => {
        set({ isLoading: true, error: null });
        try {
          const { error } = await supabaseClient
            .from('invoice_tenant_retention_accounts')
            .delete()
            .eq('id', id);
          if (error) throw error;

          set((state) => ({
            tenantRetentionAccounts: state.tenantRetentionAccounts.filter(a => a.id !== id),
            isLoading: false
          }));
          return { success: true };
        } catch (error) {
          console.error('Error deleting tenant retention account:', error.message);
          set({ error: error.message, isLoading: false });
          return { success: false, error: error.message };
        }
      },

      heuristicAutoMap: async (currentConfig = null, overwriteAll = false) => {
        const { accountTaxMaps, chartOfAccounts, retentionConcepts, tenantRetentionAccounts, tenantFiscalConfig } = get();
        
        // Usar configuración pasada (UI) o la guardada (Estado)
        const activeConfig = currentConfig || tenantFiscalConfig;
        const ivaExcludeRanges = activeConfig?.heuristic_iva_exclude_ranges;
        const defaultIvaAccount = activeConfig?.cuenta_iva_descontable;

        const userId = getUserId();
        
        if (!accountTaxMaps.length || !chartOfAccounts.length) return { success: false, message: 'No hay datos suficientes' };
        
        const isInRange = (code, rangeString) => {
          if (!rangeString) return false;
          const codeNum = parseInt(code);
          const parts = rangeString.split(',').map(p => p.trim());
          for (const part of parts) {
            if (part.includes('-')) {
              const [start, end] = part.split('-').map(n => parseInt(n));
              if (codeNum >= start && codeNum <= end) return true;
            } else {
              if (codeNum === parseInt(part)) return true;
            }
          }
          return false;
        };

        const accountsLookup = {};
        chartOfAccounts.forEach(a => { accountsLookup[String(a.account_code)] = a; });

        // Map categories and concept keywords
        const RULES = [
          { keywords: /HONORARIO|ASESOR|PROFESIONAL|JURIDIC|CONTABLE/i, category: 'honorarios', conceptMatch: /honorario/i, vatKeywords: /GASTO|HONORARIO/i },
          { keywords: /COMISION/i, category: 'services', conceptMatch: /comision/i, vatKeywords: /SERVICIO|COMISION/i },
          { keywords: /ARRENDAMIENTO|ALQUILER|CANON/i, category: 'arrendamientos', conceptMatch: /arren/i, vatKeywords: /GASTO|ARRENDAMIENTO/i },
          { keywords: /TRANSPORTE.*CARGA|CARGA|FLETE/i, category: 'transporte_carga', conceptMatch: /carga/i, vatKeywords: /SERVICIO|CARGA/i },
          { keywords: /TRANSPORTE.*PASAJERO|TIQUETE|VIATICO/i, category: 'transporte_pasajeros', conceptMatch: /pasajero/i, vatKeywords: /GASTO|PASAJERO/i },
          { keywords: /ASEO|VIGILANCIA|MANTENIMIENTO|REPARACION|LOGISTIC|APOYO|PUBLICIDAD|SERVICIO|SUMINISTRO/i, category: 'services', conceptMatch: /servicio/i, vatKeywords: /SERVICIO/i },
          { keywords: /COMPRA|PAPELERIA|DOTACION|MATERIAL|INSUMO|REPUESTO|MERCANCIA|ADQUISICION/i, category: 'purchases', conceptMatch: /compra|rco/i, vatKeywords: /COMPRA|INVENTARIO/i },
          { keywords: /SOFTWARE|WEB|NUBE|HOSTING/i, category: 'servicios_software', conceptMatch: /servicio/i, vatKeywords: /SERVICIO/i },
          { keywords: /DIVIDENDO/i, category: 'dividendos', conceptMatch: /dividendo/i, vatKeywords: /GASTO|DIVIDENDO/i },
          { keywords: /PRODUCCION|OBRA|COSTO.*DIRECTO|MANO.*OBRA/i, category: 'services', conceptMatch: /servicio/i, vatKeywords: /SERVICIO/i },
        ];

        // 1. Better Concept Discovery
        const discoveredRetentionAccounts = [];
        // Creamos un set de las cuentas ya mapeadas, no de los conceptos, para permitir múltiples cuentas al mismo concepto
        const existingMappedAccounts = new Set(tenantRetentionAccounts.map(tra => String(tra.account_code)));

        if (overwriteAll || tenantRetentionAccounts.filter(tra => tra.tenant_user_id === userId).length < 5) {
          for (const concept of retentionConcepts) {
            
            // if we are not overwriting, and the tenant already has this concept configured, skip
            if (!overwriteAll) {
              const existing = tenantRetentionAccounts.find(tra => tra.retention_concept_id === concept.id && tra.tenant_user_id === userId);
              if (existing) continue;
            }

            const prefix = concept.tax_type === 'retefuente' ? '2365' : (concept.tax_type === 'reteiva' ? '2367' : '2368');
            let searchKeywords = [];
            let excludeKeywords = [];
            
            const cName = concept.name.toLowerCase();
            const cCode = concept.code.toLowerCase();

            // Extracción dinámica de palabras clave significativas del nombre del concepto DIAN
            const conceptWords = concept.name
              .split(/[\s,y,de,a,en]+/)
              .map(w => w.trim().replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ]/g, '').replace(/es|s$/, '')) // Remueve caracteres especiales y plurales básicos
              .filter(w => w.length > 4)
              .map(w => new RegExp(w, 'i'));

            if (cCode.includes('services') || cName.includes('servicio')) {
              searchKeywords = [/servicio/i, ...conceptWords];
              if (cName.includes('restaurante')) searchKeywords.push(/rest/i); // Atrapa "REST."
              if (!cName.includes('flete')) excludeKeywords = [/flete/i];
            } else if (cCode.includes('purchases') || cName.includes('compra')) {
              searchKeywords = [/compra|rco/i, ...conceptWords];
            } else if (cCode.includes('honorarios') || cName.includes('honorario')) {
              searchKeywords = [/honorario|asesor/i, ...conceptWords];
            } else if (cCode.includes('arren') || cName.includes('arren')) {
              searchKeywords = [/arren/i, ...conceptWords];
            } else if (cCode.includes('comis') || cName.includes('comis')) {
              searchKeywords = [/comis/i, ...conceptWords];
            } else {
              searchKeywords = [new RegExp(concept.name.split(' ')[0], 'i'), ...conceptWords];
            }

            // Target rate for matching (e.g., 4, 1, 2.5)
            let rateValue = concept.tarifa_declarante || parseFloat(concept.name.match(/[\d.]+/)?.[0]);
            if (rateValue != null && rateValue < 1) rateValue = parseFloat((rateValue * 100).toFixed(4)); // Float precision fix (0.035*100 = 3.5)

            // Deduplicar searchKeywords
            searchKeywords = searchKeywords.filter((kw, index, self) => 
              index === self.findIndex((t) => t.source === kw.source)
            );

            const candidates = chartOfAccounts
              .filter(a => a.account_code.toString().startsWith(prefix))
              .map(a => {
                let score = 0;
                const desc = String(a.description || '').toLowerCase();
                
                // Bonificación por palabras clave dinámicas
                searchKeywords.forEach(kw => {
                  if (kw.test(desc)) score += 15;
                });
                
                if (excludeKeywords.some(kw => kw.test(desc))) score -= 50;

                if (rateValue != null) {
                  const targetRateStr = rateValue.toString();
                  // Match "4%", " 4 ", " 4.0" (soporte para final de string o espacios post %)
                  const rateRegex = new RegExp(`(\\s|\\b)${targetRateStr.replace('.', '\\.')}(%|\\s|\\b|$)`, 'i');
                  
                  if (rateRegex.test(desc)) {
                    score += 100; // HUGE bonus for matching rate
                  } else {
                    // CRITICAL: Penalty if the description contains a DIFFERENT percentage
                    const anyRateMatch = desc.match(/(\d+(\.\d+)?)%/);
                    if (anyRateMatch && anyRateMatch[1] !== targetRateStr) {
                      score -= 200; // HEAVY penalty for wrong rate
                    }
                  }
                }
                
                return { account: a, score };
              })
              .filter(c => c.score > 2) // Must match at least keywords and not be excluded
              .sort((a, b) => b.score - a.score);

            const matchInPuc = candidates.length > 0 ? candidates[0].account : null;
            
            if (matchInPuc) {
              discoveredRetentionAccounts.push({ 
                tenant_user_id: userId, 
                retention_concept_id: concept.id, 
                account_code: matchInPuc.account_code 
              });
            }
          }
        }
        // 2. Map Categories for rules
        RULES.forEach(rule => {
          const matchedConcepts = retentionConcepts.filter(c => rule.conceptMatch.test(c.name) || rule.conceptMatch.test(c.code));
          const allRetentionAccounts = [...tenantRetentionAccounts, ...discoveredRetentionAccounts]
            .filter(tra => tra.tenant_user_id === userId);

          const preferredConcept = matchedConcepts.find(c => 
            allRetentionAccounts.some(tra => tra.retention_concept_id === c.id)
          ) || matchedConcepts[0];
          rule.suggestedConceptId = preferredConcept?.id || null;
        });

        // 3. Update Account Tax Maps
        const updates = [];
        accountTaxMaps.forEach(mapRow => {
          const account = accountsLookup[String(mapRow.account_code)];
          if (!account) return;

          const desc = String(account.description || '');
          const codeStr = mapRow.account_code.toString();
          const dataToUpdate = {};

          // --- RANGE-BASED RULES ---
          // A. Sueldos y Gastos de Personal (5105 / 5205 / 7105 / 7205) -> No IVA, No ReteFuente
          if (/^(5105|5205|7105|7205)/.test(codeStr)) {
            if (mapRow.applies_vat !== false) dataToUpdate.applies_vat = false;
            if (mapRow.retention_concept_id !== null) dataToUpdate.retention_concept_id = null;
          }
          // B. Impuestos (5115 / 5215 / 7115 / 7215) -> No IVA, No ReteFuente
          else if (/^(5115|5215|7115|7215)/.test(codeStr)) {
            if (mapRow.applies_vat !== false) dataToUpdate.applies_vat = false;
            if (mapRow.retention_concept_id !== null) dataToUpdate.retention_concept_id = null;
          }
          // C. General Expenses / Assets (Starts with 1, 5, 6, 7)
          else if (/^[1567]/.test(codeStr)) {
            const rule = RULES.find(r => r.keywords.test(desc));
            
            // Suggest VAT Account if empty
            if ((overwriteAll || !mapRow.vat_account_code) && !/IVA/i.test(desc)) {
              let suggestedVat = null;
              
              // Súper flexible: buscar en la raíz 2408 O cualquier cuenta que incluya "IVA" en el nombre
              const ivaAccounts = chartOfAccounts.filter(a => {
                const isIva = String(a.account_code).startsWith('2408') || /IVA/i.test(a.description || '');
                if (!isIva) return false;

                // Ignorar cuentas de retención (clase 2367) aunque digan IVA
                if (String(a.account_code).startsWith('2367')) return false;

                // Aplicar rangos de exclusión configurados
                if (ivaExcludeRanges) {
                  if (isInRange(a.account_code, ivaExcludeRanges)) return false;
                }
                
                return true;
              });

              // 1. Intentar encontrar cuenta de IVA más específica basada en las reglas
              if (rule) {
                const searchKw = rule.vatKeywords || rule.keywords;
                const specificIva = ivaAccounts.find(ia => searchKw.test(ia.description || ''));
                if (specificIva) suggestedVat = String(specificIva.account_code);
              }

              // 2. Si no hay específica, considerar el default del tenant (si no está excluido)
              if (!suggestedVat && defaultIvaAccount && !isInRange(defaultIvaAccount, ivaExcludeRanges)) {
                suggestedVat = defaultIvaAccount;
              }

              // 3. FALLBACK: Si todavía no hay sugerencia, buscar la cuenta de IVA Descontable (240802) más común disponible
              if (!suggestedVat && ivaAccounts.length > 0) {
                const commonIva = ivaAccounts.find(ia => String(ia.account_code).startsWith('240802')) || ivaAccounts[0];
                if (commonIva) suggestedVat = String(commonIva.account_code);
              }

              if (suggestedVat) {
                dataToUpdate.vat_account_code = suggestedVat;
                dataToUpdate.applies_vat = true;
              }
            }

            if (rule) {
              if (overwriteAll || mapRow.tax_category !== rule.category) dataToUpdate.tax_category = rule.category;
              if (overwriteAll || mapRow.retention_concept_id !== rule.suggestedConceptId) dataToUpdate.retention_concept_id = rule.suggestedConceptId;
              
              const icaCats = ['services', 'honorarios', 'arrendamientos', 'transporte_carga', 'transporte_pasajeros'];
              if (icaCats.includes(rule.category)) {
                if (overwriteAll || mapRow.applies_reteica !== true) dataToUpdate.applies_reteica = true;
              }
            }
          }

          if (Object.keys(dataToUpdate).length > 0) {
            updates.push({ id: mapRow.id, ...dataToUpdate });
          }
        });

        if (updates.length === 0 && discoveredRetentionAccounts.length === 0) {
          return { success: true, message: 'No se encontraron nuevas sugerencias.', count: 0, discovered: 0 };
        }

        set({ isLoading: true });
        try {
          // A. Save discovered retention accounts
          if (discoveredRetentionAccounts.length > 0) {
            const { data, error } = await supabaseClient
              .from('invoice_tenant_retention_accounts')
              .upsert(discoveredRetentionAccounts, { onConflict: 'tenant_user_id, retention_concept_id' })
              .select();
            if (error) throw error;
            
            // Reemplazar o agregar en el estado local de Zustand
            set((state) => {
              const newAccs = [...state.tenantRetentionAccounts];
              data.forEach(d => {
                const existingIndex = newAccs.findIndex(tra => tra.retention_concept_id === d.retention_concept_id);
                if (existingIndex >= 0) newAccs[existingIndex] = d; else newAccs.push(d);
              });
              return { tenantRetentionAccounts: newAccs };
            });
          }

          // B. Bulk update account tax maps
          if (updates.length > 0) {
            const groups = {};
            updates.forEach(upd => {
              const { id, ...data } = upd;
              const key = JSON.stringify(data);
              if (!groups[key]) groups[key] = { data, ids: [] };
              groups[key].ids.push(id);
            });

            for (const key in groups) {
              const group = groups[key];
              const { error } = await supabaseClient
                .from('invoice_account_tax_map')
                .update(group.data)
                .in('id', group.ids)
                .eq('tenant_user_id', userId);
              if (error) throw error;
            }

            set((state) => ({
              accountTaxMaps: state.accountTaxMaps.map(item => {
                const upd = updates.find(u => u.id === item.id);
                return upd ? { ...item, ...upd } : item;
              })
            }));
          }

          set({ isLoading: false });
          return { success: true, count: updates.length, discovered: discoveredRetentionAccounts.length };
        } catch (err) {
          console.error('Error in heuristic auto-map:', err);
          set({ error: err.message, isLoading: false });
          return { success: false, error: err.message };
        }
      }
    }));

    return store;
  } catch (error) {
    console.error('Error creating fiscal store:', error);
    throw error;
  }
};

export default createFiscalStore;
